import { useCallback, useMemo } from 'react';
import { PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsHooks, TabbedTable } from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { emptyStateGraphics, TableColumnConfig } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../hooks/use-show-bulk-message-button';
import { trackingIds } from '../../tracking-ids';
import { drillDownTableSize } from '../../views/common-styles';
import { BulkMessageButton } from './bulk-message-button';
import { isPatientScheduled } from './helpers/is-patient-scheduled';
import { collectPatientIds, deriveExportValue } from './helpers/patients';
import { useSubViewTableColConfig } from './helpers/subview-table-col-config';
import { usePatientsLastContacted } from './hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { SmallModePatientsList } from './small-mode-patients-list';

type Tab = 'all' | 'unscheduled';

type Props = {
  data?: PracticeAnalyticsTypes.PatientInfo[];
  isExportEnabled?: boolean;
  isLoading?: boolean;
  isSmallMode?: boolean;
  locationId?: string;
  tab?: string;
};

type CategorizedPatients = {
  all: PracticeAnalyticsTypes.PatientInfo[];
  unscheduled: PracticeAnalyticsTypes.PatientInfo[];
};

type LargeTableProps = PracticeAnalyticsTypes.LargeModeTableProps<Tab> & {
  predefinedLocationId?: string;
};

export const PractitionerAnalysisPatients = ({
  data = [],
  isExportEnabled,
  isLoading,
  isSmallMode,
  locationId,
  tab,
}: Props) => {
  const { t } = useTranslation('analytics');
  const { filters, isDemoAccount } = usePracticeAnalyticsShallowStore('filters', 'isDemoAccount');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const isBulkMessagingEnabled = useShowBulkMessageButton();
  const patientIds = useMemo(() => collectPatientIds(data), [data]);
  const { lastContactedDates, isLoading: isLoadingLastContacted } = usePatientsLastContacted({ patientIds });

  const categorizedPatients: CategorizedPatients = useMemo(() => {
    return data.reduce<CategorizedPatients>(
      (final, patient) => {
        const value = {
          ...final,
          all: [...final.all, patient],
        };

        if (!isPatientScheduled(patient)) {
          value.unscheduled.push(patient);
        }

        return value;
      },
      { all: [], unscheduled: [] }
    );
  }, [data]);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'Diagnosed',
          'Last Contacted',
          '$ Value',
          'Procedure Codes',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            lastContactedDates,
            locationNames,
          }),
        sortColumn: 'productionAmount',
      }),
    [filters.locations, lastContactedDates, locationNames]
  );

  const tabs = useMemo(
    () => ({
      all: {
        data: categorizedPatients.all,
        label: t('All'),
        trackingId: trackingIds.practiceAnalytics.practitionerAllTab,
      },
      unscheduled: {
        data: categorizedPatients.unscheduled,
        label: t('Unscheduled'),
        trackingId: trackingIds.practiceAnalytics.practitionerUnscheduledTab,
      },
    }),
    [categorizedPatients.all, categorizedPatients.unscheduled, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: `Practitioner Analysis Patients - ${tab}`, // Translation not needed
    initialTab: 'all',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: LargeTableProps = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
    predefinedLocationId: locationId,
  };

  return isSmallMode ? (
    <SmallModePatientsList<Tab> {...smallModeTableProps} trackingIdBase={`practitioner-analysis-small-mode-${tab}`} />
  ) : (
    <LargeModeTable {...largeModeTableProps} />
  );
};

const LargeModeTable = ({
  isBulkMessagingEnabled,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  predefinedLocationId,
  processExportableData,
  tabs,
  ...rest
}: LargeTableProps) => {
  const { t } = useTranslation('analytics');

  const middleColumns: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = [
    {
      Header: t('Diagnosed'),
      headerLabel: t('Diagnosed'),
      accessor: ({ diagnosed }) => formatters.date(diagnosed),
      id: 'diagnosed',
      sortType: 'date',
      sortInverted: true,
      width: 200,
    },
  ];

  const tabsCommonConfig = {
    colConfig: useSubViewTableColConfig({
      actionsTrackingIdBase: 'practitioner-analysis-patient',
      hasProcedures: true,
      middleColumns,
      patientsLastContacted: {
        isLoading: isLoadingLastContacted,
        lastContactedDates: rest.lastContactedDates,
      },
      predefinedLocationId,
    }),
    emptyStateConfig: 'users' as keyof typeof emptyStateGraphics,
    tableInstanceId: 'practitioner-analysis-patients',
  };

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      {...rest}
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      processExportableData={processExportableData}
      showExportIcon={isExportEnabled}
      tabs={{
        all: {
          ...tabsCommonConfig,
          ...tabs.all,
        },
        unscheduled: {
          ...tabsCommonConfig,
          ...tabs.unscheduled,
        },
      }}
      trackingIdBase='practitioner-analysis-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
